import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import './JourneyBackButton.css';

interface JourneyBackButtonProps {
  className?: string;
  variant?: 'default' | 'compact' | 'floating';
}

/**
 * A reusable button component that allows users to navigate back to their
 * startup journey from pages like survey creation or pre-release project creation.
 * 
 * The button automatically detects if the user came from a journey context
 * by checking for the 'journeyId' or 'stepId' query parameters.
 */
export const JourneyBackButton: React.FC<JourneyBackButtonProps> = ({ 
  className = '', 
  variant = 'default' 
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  
  const journeyId = searchParams.get('journeyId');
  const stepId = searchParams.get('stepId');
  const source = searchParams.get('source');
  
  // Only show the button if we have journey context
  const showButton = journeyId || (source === 'journey' && stepId);
  
  if (!showButton) {
    return null;
  }
  
  const handleBackToJourney = () => {
    if (journeyId) {
      navigate(`/journey/${journeyId}`);
    } else {
      // Fallback: try to go back in history
      navigate(-1);
    }
  };
  
  const buttonClasses = `journey-back-button journey-back-button--${variant} ${className}`;
  
  return (
    <button
      type="button"
      onClick={handleBackToJourney}
      className={buttonClasses}
      title={t('journey.backToJourney')}
    >
      <i className="bi bi-arrow-left-circle"></i>
      <span className="journey-back-button__text">{t('journey.backToJourney')}</span>
    </button>
  );
};
